import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import * as Yup from 'yup';

import { login } from '../services/auth';
import useTitle from '../hooks/useTitle';
import { errorDetails } from '../utils/statusCodes';
import { createErrorNotification } from '../utils/createErrorNotification';
import { useAuth, authStatus } from '../context/auth/AuthContext';

const LoginView = () => {
  const [values, setValues] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { t } = useTranslation('promptbar');

  const { setStatus, checkAuth } = useAuth();

  const title = t('promptbar:loginMemas');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  const loginSchema = Yup.object({
    username: Yup.string()
      .min(3, t('usernameMin'))
      .max(50, t('usernameMax'))
      .matches(/^[a-zA-Z0-9._-]+$/, t('usernameFormat'))
      .required(t('usernameRequired')),
    password: Yup.string()
      .min(8, t('passwordMin'))
      .required(t('passwordRequired')),
  });

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      await loginSchema.validate(values, { abortEarly: false });

      const formData = new URLSearchParams();
      formData.append('username', values.username);
      formData.append('password', values.password);

      await login(formData);
      await checkAuth();
    } catch (error) {
      if (error.name === 'ValidationError') {
        console.error('Ошибка валидации:', error.message);
        const validationError = {};

        error.inner.forEach(err => {
          validationError[err.path] = err.message;
        });
        setErrors(validationError);
        return;
      }

      // Backend errors
      // const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      // 400: invalid credentials
      if (detail === errorDetails.LOGIN_BAD_CREDENTIALS) {
        createErrorNotification(t('invalidCredentials'));
        return;
      }

      createErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = async evt => {
    const { name, value } = evt.target;
    const newValues = { ...values, [name]: value };
    setValues(newValues);

    try {
      await loginSchema.validateAt(name, newValues);
      setErrors(prev => ({ ...prev, [name]: '' }));
    } catch (err) {
      setErrors(prev => ({ ...prev, [name]: err.message }));
    }
  };

  const handleShowPassword = () => setShowPassword(prev => !prev);

  const isFormValid =
    Object.values(errors).every(err => !err) &&
    Object.values(values).every(Boolean);

  return (
    <Box width={500} margin={'5% auto 0'}>
      <Typography variant="h1" align="center">
        {title}
      </Typography>

      <Box pt={3}>
        <Paper elevation={3}>
          <Box sx={{ padding: '3rem 2rem 2rem' }}>
            <Box
              component="form"
              autoComplete="off"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '2.5rem',
              }}
            >
              <FormControl fullWidth variant="standard">
                <InputLabel htmlFor="TextField-1" shrink={false}>
                  {t('username')}
                </InputLabel>
                <TextField
                  id="TextField-1"
                  autoFocus
                  name="username"
                  value={values.username}
                  onChange={handleChange}
                  error={Boolean(errors.username)}
                  helperText={errors.username}
                />
              </FormControl>

              <FormControl fullWidth variant="standard">
                <InputLabel htmlFor="TextField-2" shrink={false}>
                  {t('password')}
                </InputLabel>
                <TextField
                  id="TextField-2"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  onChange={handleChange}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowPassword}
                          edge="end"
                          size="small"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <Button
                type="button"
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid || isLoading}
              >
                {t('login')}
                {isLoading && (
                  <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                )}
              </Button>

              <Typography variant="h3" sx={{ display: 'flex', gap: '7px' }}>
                {t('noAccount')}?
                <Link to={'/signup'} className="MuiLink">
                  {t('signup')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginView;
